// Link (audit §4b): a secondary action is an underlined word (DESIGN.md §3a): Clear, Add helper, Open job,
// Move. With href it is a link; without, a button drawn as one (it does something here rather than going
// somewhere). Danger is red, for the one that can't be undone. It takes the type size of the line it's in.
// Size touch-on-phone is for a link that stands alone as an action (a side panel's Open the job): at
// phone widths it is a touch target, 44 tall, as the library's touch controls are. Reads only --link-*.
import type { ReactNode } from 'react';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import './Link.css';

export interface LinkProps {
  children: ReactNode;
  href?: string;
  onPress?: () => void;
  variant?: 'inline' | 'danger';
  /** inline: the words alone; touch-on-phone: a 44 tall target at phone widths. */
  size?: 'inline' | 'touch-on-phone';
  isDisabled?: boolean;
  /** With href: a file or another site, opened in a new tab so the page stays where it is. */
  isExternal?: boolean;
  /** Its name for screen readers where its words repeat down a list ("Add a rate to British Columbia"). */
  label?: string;
}

export function Link({
  children,
  href,
  onPress,
  variant = 'inline',
  size = 'inline',
  isDisabled,
  isExternal,
  label,
}: LinkProps) {
  const sized = size === 'inline' ? undefined : size;
  if (href !== undefined)
    return (
      <AriaLink
        className="fl-link"
        data-variant={variant}
        data-size={sized}
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noreferrer' } : {})}
        onPress={onPress}
        isDisabled={isDisabled}
        {...(label ? { 'aria-label': label } : {})}
      >
        {children}
      </AriaLink>
    );
  return (
    <AriaButton
      className="fl-link"
      data-variant={variant}
      data-size={sized}
      onPress={onPress}
      isDisabled={isDisabled}
      {...(label ? { 'aria-label': label } : {})}
    >
      {children}
    </AriaButton>
  );
}
